import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * PUBLIC invitation landing page (route: /invite).
 *
 * Reached from a distributor's QR / invitation link:
 *   https://<web-domain>/invite?distributorId=<id>&distributor_email=<email>
 *
 * Behaviour:
 *  - On iOS/Android, the OS opens the app DIRECTLY (Universal Link / App Link)
 *    when it is installed and the domain is verified (AASA / assetlinks.json),
 *    so this page is only ever seen when the app is NOT installed.
 *  - As a belt-and-braces fallback we also try the custom scheme
 *    (tellarnieapp://) and, if that fails, send the user to the correct store
 *    with distributorId preserved (Android install referrer).
 *
 * The page is intentionally self-contained (no backend call) so it works for
 * any prospective user before they have an account/token.
 */

// TODO: replace with the real store listings once published.
const IOS_STORE_URL = "https://apps.apple.com/app/id0000000000";
const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.projectsetups";

function getPlatform() {
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  return "other";
}

export default function Invite() {
  const [searchParams] = useSearchParams();
  const distributorId = searchParams.get("distributorId") || "";
  const distributorEmail = searchParams.get("distributor_email") || "";

  const platform = useMemo(() => getPlatform(), []);

  // Store URL that preserves distributorId. On Android the referrer is
  // delivered to the app via the Install Referrer API after install.
  const storeUrlWithReferrer = useMemo(() => {
    if (platform === "android") {
      const referrer = encodeURIComponent(`distributorId=${distributorId}`);
      return `${ANDROID_STORE_URL}&referrer=${referrer}`;
    }
    return IOS_STORE_URL;
  }, [platform, distributorId]);

  // Try to open the app; fall back to the store if it is not installed.
  useEffect(() => {
    if (platform === "other") return;
    const params = new URLSearchParams();
    if (distributorId) params.set("distributorId", distributorId);
    if (distributorEmail) params.set("distributor_email", distributorEmail);
    const deepLink = `tellarnieapp://invite?${params.toString()}`;

    const start = Date.now();
    const timer = setTimeout(() => {
      if (Date.now() - start < 2500) {
        window.location.href = storeUrlWithReferrer;
      }
    }, 1200);

    window.location.href = deepLink;
    return () => clearTimeout(timer);
  }, [platform, distributorId, distributorEmail, storeUrlWithReferrer]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h2>Welcome to Tell Arnie</h2>
      <p style={{ color: "#555", maxWidth: 420 }}>
        Opening the Tell Arnie app… If it doesn't open automatically, install it
        below. Your supplier will be set up for you automatically.
      </p>
      <a
        href={storeUrlWithReferrer}
        style={{
          marginTop: 16,
          background: "#28a745",
          color: "#fff",
          padding: "12px 28px",
          borderRadius: 6,
          textDecoration: "none",
          fontWeight: "bold",
        }}
      >
        Get the app
      </a>
    </div>
  );
}
